import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getData } from "../../apis";
import { API } from "../../apis/config";
import LoadingScreen from "../../components/LoadingScreen";
import ScrollToTopButton from "../../components/ScrollToTopButton";
import { FestaProduct } from "../../interfaces/FestaProduct";
import { ModalInfo } from "../../interfaces/ModalInfo";
import { RootState } from "../../stores";
import { set } from "../../stores/slices/festaSlice";
import {
  changeHeaderFilter,
  convertFilterIcon,
} from "../../utils/headerFilter";
import { matchesProductNameSearch } from "../../utils/koreanSearch";
import ProductModal from "./ProductModal";
import SearchProductInput from "../../components/SearchProductInput";
import logo from "../../assets/images/spiel_logo.png";
import { COLORS } from "./styles";

function SearchProduct() {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [modal, setModal] = useState<ModalInfo<FestaProduct>>({
    isOpen: false,
    selected: undefined,
  });
  const dispatch = useDispatch();
  const searchRows = useSelector(
    (state: RootState) => state.festaProduct.searchRows,
  );
  const [sortedData, setSortedData] = useState<FestaProduct[]>([]);
  const [headerFilter] = useState<{ [key: string]: number }>({});
  const productSliceData = useSelector(
    (state: RootState) => state.festaProduct.searchRows,
  );

  const Header = {
    Name: "name",
    Location1: "location1",
    Location2: "location2",
    Location3: "location3",
    Sleeve: "sleeve",
    Note: "note",
  } as const;

  const clickHeader = (
    key: string,
    compareFn: (a: FestaProduct, b: FestaProduct) => number,
  ) => {
    changeHeaderFilter(headerFilter, setSortedData, key, compareFn, searchRows);
  };

  const createFilterIcon = (key: string): string => {
    return convertFilterIcon(headerFilter, key);
  };

  const createLocationComparator = (index: number) => (a: any, b: any) => {
    const valA = (a.location.split("/")[index] || "").trim();
    const valB = (b.location.split("/")[index] || "").trim();

    // 값이 없는 경우 아래로
    if (!valA && !valB) return 0;
    if (!valA) return 1;
    if (!valB) return -1;

    return valA.localeCompare(valB, "ko", {
      numeric: true,
      sensitivity: "base",
    });
  };

  useEffect(() => {
    const data = async () => {
      await getData<FestaProduct[]>(API.FESTA_STAFF.PRODUCTS)
        .then((res) => {
          dispatch(set(res.result.filter((value) => value.searchable)));
        })
        .finally(() => {
          setIsLoading(false);
        });
    };
    data();
  }, [dispatch]);

  useEffect(() => {
    if (sortedData.length === 0) {
      setSortedData(productSliceData);
    }
  }, [dispatch, productSliceData, sortedData.length]);

  return (
    <div>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <div className="md:mx-20">
          <ScrollToTopButton />

          <ProductModal
            modal={modal}
            setModal={(isOpen, selected) => {
              setModal({ isOpen, selected });
            }}
          />

          <div className="relative flex items-center justify-center mt-6 w-full">
            {/* 로고는 절대 위치로 고정 */}
            <img
              src={logo}
              alt="home"
              width="32"
              height="32"
              className="absolute left-3"
            />
            <span className="text-2xl font-bold text-white">
              파주슈필 매장 검색
            </span>
          </div>

          <SearchProductInput
            searchKeyword={searchKeyword}
            setSearchKeyword={setSearchKeyword}
          />
          {/* <span className="text-sm">
            상품명에 밑줄이 있다면 증정품이 있는 상품입니다.
          </span>
          <br /> */}
          <span className="text-sm text-white">
            각 헤더(상품명, 위치)를 선택하면 정렬할 수 있습니다.
            <br />
            '체험존'은 해당 게임의 체험 공간에서 판매하는 것을 말합니다.
          </span>
          <br />
          <table className="my-4 w-full bg-white ">
            <thead className="bg-white border-none">
              <tr>
                <th
                  className={`w-5/12 py-2 text-center ${COLORS.TABLE_HEADER} cursor-pointer`}
                  onClick={() => {
                    clickHeader(Header.Name, (a, b) =>
                      a.name.localeCompare(b.name, "ko"),
                    );
                  }}
                >
                  상품명{createFilterIcon(Header.Name)}
                </th>
                {/* 본점 컬럼 */}
                <th
                  className={`py-2 text-center ${COLORS.TABLE_HEADER} cursor-pointer`}
                  onClick={() => {
                    clickHeader(Header.Location1, createLocationComparator(0));
                  }}
                >
                  본점{createFilterIcon(Header.Location1)}
                </th>

                {/* 2호점 컬럼 */}
                <th
                  className={`py-2 text-center ${COLORS.TABLE_HEADER} cursor-pointer`}
                  onClick={() => {
                    clickHeader(Header.Location2, createLocationComparator(1));
                  }}
                >
                  2호점{createFilterIcon(Header.Location2)}
                </th>

                {/* 체험존 판매 컬럼 */}
                <th
                  className={`py-2 text-center ${COLORS.TABLE_HEADER} cursor-pointer`}
                  onClick={() => {
                    clickHeader(Header.Location3, createLocationComparator(2));
                  }}
                >
                  기타{createFilterIcon(Header.Location3)}
                </th>
                {/* <th
                  className="w-2/12 px-4 py-2 text-center text-gray-600 cursor-pointer"
                  onClick={() => {
                    clickHeader(Header.Note, (a, b) =>
                      a.sleeve.localeCompare(b.sleeve, "ko")
                    );
                  }}
                >
                  슬리브{createFilterIcon(Header.Sleeve)}
                </th> */}
                {/* <th
                  className={`w-3/12 px-4 py-2 text-center ${COLORS.TABLE_HEADER} cursor-pointer`}
                  onClick={() => {
                    clickHeader(Header.Note, (a, b) =>
                      a.note === null
                        ? 1
                        : b.note === null
                          ? -1
                          : a.note.localeCompare(b.note, "ko"),
                    );
                  }}
                >
                  비고{createFilterIcon(Header.Note)}
                </th> */}
              </tr>
            </thead>
            <tbody>
              {sortedData
                .filter((item) =>
                  matchesProductNameSearch(item.name ?? "", searchKeyword),
                )
                .map((item, key) => (
                  <tr
                    className={` hover:bg-gray-100 border-t ${
                      item.note ? "underline" : ""
                    }`}
                    key={key}
                  >
                    <td className="px-4 py-2 text-sm md:text-base">
                      {item.name}
                    </td>
                    {/* 본점 데이터 칸 */}
                    <td className="px-4 py-2 text-center">
                      {item.location.split("/")[0] || "-"}
                    </td>

                    {/* 2호점 데이터 칸 */}
                    <td className="px-4 py-2 text-center">
                      {item.location.split("/")[1] || "-"}
                    </td>

                    {/* 그 외 매장 데이터 칸 */}
                    <td className="px-4 py-2 text-center text-xs sm:text-sm">
                      {item.location.split("/")[2] || "-"}
                    </td>
                    {/* <td className="px-4 py-2" onClick={() => openModal(item)}>
                      {item.sleeve}
                    </td> */}
                    {/* <td
                      className="px-4 py-2 text-sm"
                      onClick={() => openModal(item)}
                    >
                      {item.note}
                    </td> */}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SearchProduct;
